import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  useAuth,
} from '@aaas/shared';
import rejectionReasonService from '../services/rejection-reason.service';
import type {
  RejectionReason,
  RejectionReasonCreateInput,
  RejectionReasonUpdateInput,
} from '../types/rejection-reason';

interface RejectionReasonFormDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  rejectionReason?: RejectionReason | null;
  onSuccess: () => void;
}

export function RejectionReasonFormDialog({
  open,
  onClose,
  mode,
  rejectionReason,
  onSuccess,
}: RejectionReasonFormDialogProps) {
  const { user, currentWorkspace } = useAuth();
  const { t } = useTranslation('ats');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens or rejectionReason changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && rejectionReason) {
        setReason(rejectionReason.reason);
      } else {
        setReason('');
      }
      setError(null);
    }
  }, [open, mode, rejectionReason]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !currentWorkspace) return;
    if (!reason.trim()) {
      setError(t('rejectionReason.reasonLabel'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await user.getIdToken();

      if (mode === 'create') {
        const data: RejectionReasonCreateInput = {
          reason: reason.trim(),
        };
        await rejectionReasonService.createRejectionReason(currentWorkspace.id, token, data);
      } else if (rejectionReason) {
        const data: RejectionReasonUpdateInput = {
          reason: reason.trim(),
        };
        await rejectionReasonService.updateRejectionReason(currentWorkspace.id, rejectionReason.id, token, data);
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} rejection reason:`, err);
      const errorMessage = err instanceof Error ? err.message : t('config.unknownError');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create'
              ? t('rejectionReason.createTitle')
              : t('rejectionReason.editTitle')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reason">{t('rejectionReason.reasonLabel')}</Label>
            <Input
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('rejectionReason.reasonPlaceholder')}
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              {t('config.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.creating')}
                </>
              ) : mode === 'create' ? (
                t('rejectionReason.createButton')
              ) : (
                t('rejectionReason.updateButton')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
